import React, { useState } from 'react';
import {
    Box,
    Checkbox,
    Collapse,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Close as CloseIcon,
    ExpandLess,
    ExpandMore,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import DebouncedLoader from '../common/DebouncedLoader';
import { useBrands } from '../../graphql/pimcore/modules/resourceLibrary/contexts/BrandsProvider';
import { useProductGroups } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ProductGroupsProvider';
import { useProductApplications } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ProductApplicationsProvider';
import { useResourceTypes } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceTypesProvider';
import { useResourceLibraryItems } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceLibraryItemsProvider';
import {
    filterDescriptors,
    FilterName,
    Filters,
} from '../../graphql/pimcore/modules/resourceLibrary/types/filters';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { useCommonContent } from '../../graphql/pimcore/modules/siteContent/hooks/useCommonContent';

type MenuItemData = {
    id: string;
    name: string;
    children?: MenuItemData[];
    count?: number;
};

type Category = {
    key: string;
    name: string;
    filterName: FilterName;
    items: MenuItemData[];
};

interface LibraryDrawerProps {
    openDrawer?: boolean;
    setOpenDrawer?: React.Dispatch<React.SetStateAction<boolean>>;
    /** Show counts next to category names */
    showCounts?: boolean;
}

const LibraryDrawer: React.FC<LibraryDrawerProps> = ({
    openDrawer,
    setOpenDrawer,
    showCounts = true,
}) => {
    const { brands, loading: brandsLoading } = useBrands();
    const { productGroups, loading: productGroupsLoading } = useProductGroups();
    const { productApplications, loading: productApplicationsLoading } = useProductApplications();
    const { resourceTypes, loading: resourceTypesLoading } = useResourceTypes();
    const { contentTree: cmsContent } = useCommonContent('ResourceLibrary');

    const { filters, toggleFilter, counts, refetch, resetFilters } = useResourceLibraryItems();

    const [openStates, setOpenStates] = useState<{ [key: string]: boolean }>({});
    const { isScreenMd, isScreenSm, isScreenXs } = useResponsive();
    const isSmallScreen = isScreenMd || isScreenSm || isScreenXs;

    const handleReset = () => {
        refetch();
        resetFilters();
    };

    const mapToMenuItems = <T extends { id: string; name: string; children?: T[] }>(
        items: T[],
        countsMap: Map<string, number>
    ): MenuItemData[] => {
        return items
            .filter((item) => countsMap.get(item.id) || 0 !== 0)
            .map((item) => ({
                id: item.id,
                name: item.name,
                count: countsMap.get(item.id) || 0,
                children: item.children ? mapToMenuItems(item.children, countsMap) : undefined,
            }));
    };

    const brandsMenuItems = brands ? mapToMenuItems(brands, counts.brands) : [];
    const productGroupsMenuItems = productGroups
        ? mapToMenuItems(productGroups, counts.productGroups)
        : [];
    const productApplicationsMenuItems = productApplications
        ? mapToMenuItems(productApplications, counts.productApplications)
        : [];
    const resourceTypesMenuItems = resourceTypes
        ? mapToMenuItems(resourceTypes, counts.resourceTypes)
        : [];

    const categories: Category[] = [
        {
            key: 'products',
            name: cmsContent.categories.products.content,
            filterName: 'productGroupIds',
            items: productGroupsMenuItems,
        },
        {
            key: 'applications',
            name: cmsContent.categories.applications.content,
            filterName: 'productApplicationIds',
            items: productApplicationsMenuItems,
        },
        {
            key: 'resourceTypes',
            name: cmsContent.categories.resourceType.content,
            filterName: 'resourceTypeIds',
            items: resourceTypesMenuItems,
        },
        {
            key: 'brands',
            name: cmsContent.categories.brands.content,
            filterName: 'brandId',
            items: brandsMenuItems,
        },
    ];

    const handleClick = (id: string) => {
        setOpenStates((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const isChecked = (
        filterName: keyof typeof filterDescriptors,
        currentFilters: Filters,
        item: MenuItemData
    ) => {
        let checked = false;
        if (filterDescriptors[filterName].isArray) {
            const filterValues = currentFilters[filterName] as string[];
            checked = filterValues.includes(item.id);
        } else {
            const filterValue = currentFilters[filterName] as string | null;
            checked = filterValue === item.id;
        }
        return checked;
    };

    const toggleParentAndChildrenFilters = (filterName: FilterName, item: MenuItemData) => {
        const isParentChecked = isChecked(filterName, filters, item);
        toggleFilter(filterName, item.id);
        item.children
            ?.filter((c) => isParentChecked === isChecked(filterName, filters, c))
            .forEach((c) => toggleFilter(filterName, c.id));

        setOpenStates((prev) => {
            const wasOpen = prev[item.id] ?? false;
            if (wasOpen === isParentChecked) {
                return { ...prev, [item.id]: !prev[item.id] };
            }
            return prev;
        });
    };

    const renderMenuItems = (items: MenuItemData[], filterName: FilterName, level = 0) => {
        return items.map((item) => {
            const checked = isChecked(filterName, filters, item);
            return (
                <React.Fragment key={item.id}>
                    <ListItemButton sx={{ paddingLeft: 4 * (level + 1) }}>
                        <ListItemIcon sx={{ minWidth: '30px' }}>
                            <Checkbox
                                checked={checked}
                                sx={{
                                    color: level === 0 ? '#E5673E' : 'inherit',
                                    '&.Mui-checked': { color: level === 0 ? '#B75332' : 'black' },
                                    '&:hover': {
                                        bgcolor: 'rgba(229, 103, 62, 0.08)',
                                        '.MuiSvgIcon-root': {
                                            color: level === 0 ? '#B75332' : 'black',
                                        },
                                    },
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleParentAndChildrenFilters(filterName, item);
                                }}
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'auto',
                                    }}
                                >
                                    <span
                                        style={{ cursor: 'pointer' }}
                                        onClick={() =>
                                            toggleParentAndChildrenFilters(filterName, item)
                                        }
                                    >
                                        {showCounts ? `${item.name} (${item.count})` : item.name}
                                    </span>
                                    <span
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleClick(item.id)}
                                    >
                                        {item.children?.length ? (
                                            openStates[item.id] ? (
                                                <ExpandLess />
                                            ) : (
                                                <ExpandMore />
                                            )
                                        ) : (
                                            <div />
                                        )}
                                    </span>
                                </div>
                            }
                            sx={{ color: level === 0 ? '#B75332' : 'black' }}
                        />
                    </ListItemButton>
                    {item.children && (
                        <Collapse in={openStates[item.id]} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {renderMenuItems(item.children, filterName, level + 1)}
                            </List>
                        </Collapse>
                    )}
                </React.Fragment>
            );
        });
    };

    const isLoading =
        brandsLoading || productGroupsLoading || productApplicationsLoading || resourceTypesLoading;

    const variant = isSmallScreen ? 'temporary' : 'permanent';
    const open = isSmallScreen ? openDrawer || false : true;
    const onClose = isSmallScreen && setOpenDrawer ? () => setOpenDrawer(false) : undefined;
    const APP_BAR_HEIGHT_XS = 56; // 行動裝置常見高度
    const APP_BAR_HEIGHT_MD = 64; // 桌機常見高度
    return (
        <DebouncedLoader loading={isLoading}>
            <Drawer
                variant={variant}
                open={open}
                onClose={onClose}
                sx={{
                    // zIndex: (t) => t.zIndex.appBar + 5, // 讓 Drawer 蓋過 AppBar
                    borderRight: '0.1px solid #E5673E',
                    display: { xs: 'block', sm: 'block' },
                    height: '100%',
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: '100%',
                        maxWidth: isSmallScreen ? '476px' : '100%',
                        position: 'relative',
                        overflowY: 'auto',
                        zIndex: 0,
                    },
                    '::-webkit-scrollbar-track': {
                        background: 'white',
                    },
                    '::-webkit-scrollbar-thumb': {
                        background: '#E5673E',
                        borderRadius: '5px',
                    },
                }}
            >
                <List sx={{ width: '100%', paddingTop: 0 }}>
                    {isSmallScreen && (
                        <ListItem
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                px: 2,
                                paddingTop: 13,
                                borderBottom: '1px solid #E5673E',
                            }}
                        >
                            <ListItemText primary={cmsContent.filtersLabel.content} />

                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton onClick={handleReset} sx={{ color: '#000' }}>
                                    <RefreshIcon />
                                    <ListItemText primary={cmsContent.reset.content} />
                                </IconButton>

                                <IconButton onClick={onClose} sx={{ color: '#000' }}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </ListItem>
                    )}

                    {categories.map((category) => (
                        <div key={category.key}>
                            <ListItem disablePadding sx={{ paddingLeft: 0 }}>
                                <ListItemButton
                                    onClick={() => handleClick(category.key)}
                                    sx={{ paddingLeft: 0 }}
                                >
                                    <ListItemIcon sx={{ minWidth: '30px' }} />
                                    <ListItemText primary={category.name} />
                                    {openStates[category.key] ? <ExpandLess /> : <ExpandMore />}
                                </ListItemButton>
                            </ListItem>
                            <Collapse in={openStates[category.key]} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {renderMenuItems(category.items, category.filterName)}
                                </List>
                            </Collapse>
                        </div>
                    ))}
                </List>
            </Drawer>
        </DebouncedLoader>
    );
};

export default LibraryDrawer;
